<script setup>
    import { ref, watchEffect } from 'vue';
    import { getFromAPI, buildPageFooter, isButtonDisabled } from '../functionCollection';
    import SearchFields from './SearchFields.vue';
    import axios from 'axios';
    import Swal from 'sweetalert2';
    import AuthMediaAdd from './AuthMediaAdd.vue';

    const props = defineProps(['fetchUrl', 'deleteUrl', 'changeBikePermissionUrl', 'changeChargePermissionUrl','getSingleMediumUrl', 'changeMediumUrl']);

    const mediaList = ref(null);
    const mediumToChange = ref(null); // to remember which medium within the table needs to be edited

    // columns from auth_media we want to fetch
    const columnsToFetch = 'id,identifier,Rechnungsempfaengernummer,Teilnehmer,nupsiNumber,type,can_use_bicycle,can_use_charge,bicycle_rate_id,charging_rate_id,was_used_until';

    // for the page selection footer
    const currentPage = ref(1);
    const maxPageNumber = ref(1);
    const mediaOnPage = ref(null);
    const footerPageNumbers = ref(new Array());


    // wanted Search fields. value is null to start with empty fields. Index is the column name from the auth_media table.
    const filtersAsString = ref(''); // needs to be stringified to be used as URL parameter for the request
    const receivedFilterParameters = ref([]);
    const filterParameters = [
        {
            index: 'Rechnungsempfaengernummer',
            title: 'RE-Nummer',
            value: null,
        },
        {
            index: 'identifier',
            title: 'Zugangscode',
            value: null,
        }
    ];

    watchEffect(async () => {
        const url = getFullUrl(props.fetchUrl);
        mediaList.value = await getFromAPI(url);

        // set up the page footer
        maxPageNumber.value = mediaList.value.meta.last_page;
        mediaList.value = mediaList.value.data;
        mediaOnPage.value = mediaList.value.length;
        footerPageNumbers.value = buildPageFooter(currentPage.value, maxPageNumber.value);
    });

    // needed to fill in the URL parameters, among others for search
    function getFullUrl(url) {
        url = url.concat('/').concat(`?page=${currentPage.value}&per_page=25&columns=${columnsToFetch}&column=Rechnungsempfaengernummer&order=desc`);
        if (filtersAsString.value !== '') {
            url = url.concat('&filter=').concat(filtersAsString.value);
        }
        return url;
    }

    // needs to remove the :id placeholder from the prop and replace it with the actual ID.
    function replaceIDinUrl(url, id) {
        let formattedUrl = url.replace(':id', id);
        return formattedUrl;
    }

    // calls for new usersList whenever a page change is requested (i.e. a page button is pressed in the footer)
    async function getNewPage(newPage) {
        currentPage.value = newPage;
        if (currentPage.value < 1) {
            currentPage.value = 1;
        }
        mediaList.value = await getFromAPI(getFullUrl(props.fetchUrl));
        maxPageNumber.value = mediaList.value.meta.last_page;
        mediaList.value = mediaList.value.data;
        mediaOnPage.value = mediaList.value.length;
        // buildPageFooter(currentPage.value, maxPageNumber.value);
    }

    // parses the search criteria, removing empty search fields from query and renaming values appropriately, and executes the search
    function filterSearch(givenFields) {
        receivedFilterParameters.value = [];
        for (let i = 0; i < givenFields.length; i++) {
            if (givenFields[i].value !== null) {
                receivedFilterParameters.value.push(givenFields[i]);
            }
            if (givenFields[i].index === 'suspended') {
                if (givenFields[i].value === 'Ja' || givenFields[i].value === 'ja') {
                    givenFields[i].value = 1;
                } else if (givenFields[i].value === 'Nein' || givenFields[i].value === 'nein') {
                    givenFields[i].value = 0;
                }
            }
        }
        filtersAsString.value = JSON.stringify(receivedFilterParameters.value);
        getNewPage(1);
    }

    // A SweetAlert2 modal asking for confirmation if the medium should be deleted.
    async function deleteMedium(id) {
        Swal.fire({
            title: "Zugangsmedium löschen?",
            text: "Wenn Sie dieses Zugangsmedium löschen, ist eine Abrechnung nicht mehr möglich und wird bei erneuter Verwendung unter den Betrugsversuchen abgespeichert. Stellen Sie sicher, dass das Medium in allen anderen Systemen gelöscht bzw. gesperrt wurde!",
            icon: 'warning',
            confirmButtonText: "Löschen",
            confirmButtonColor: "#e64942",
            showCancelButton: true,
            cancelButtonText: "Abbrechen",
            focusCancel: true,
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                axios.delete(replaceIDinUrl(props.deleteUrl, id))
                .then((data) => {
                    if (data.status === 200) {
                        Swal.fire({
                            title: 'Löschen erfolgreich',
                            text: 'Zugangsmedium wurde erfolgreich gelöscht.',
                            icon: 'success',
                        }).then((result) => {
                            if (result.isConfirmed === true) {
                                window.location.reload();
                            }
                        })
                    }
                })
            }
        }).catch((error) => {
            Swal.fire({
                title: 'Löschen fehlgeschlagen!',
                text: 'Leider ist das Löschen fehlgeschlagen. Mehr Infos: ' + error.response.data.message,
                icon: 'error',
            })
        })
    }

    // attempts to change permission (yes/no) immediately after the button press.
    // Shows a corresponding SweetAlert2 modal on return.
    async function changePermission(url, id) {
        const result = await Swal.fire({
            title: "Berechtigungen ändern?",
            text: "Wollen Sie die Berechtigungen wirklich ändern?",
            icon: 'warning',
            confirmButtonText: "Ja, ändern",
            confirmButtonColor: "#e64942",
            cancelButtonText: "Abbrechen",
            showCancelButton: true,
            reverseButtons: true,
        })
        if (result.isConfirmed) {
            axios.put(replaceIDinUrl(url, id))
            .then((data) => {
            if (data) {
                if (data.status === 200) {
                    Swal.fire({
                        title: "Erfolgreich",
                        text: "Die Berechtigungen wurden erfolgreich geändert! " + data.data.message,
                        icon: 'success',
                    }).then((result) => {
                        if (result.isConfirmed === true) {
                            window.location.reload();
                        }
                    });
                } else {
                    Swal.fire({
                        title: "Fehlgeschlagen",
                        text: data.data.message,
                        icon: 'error',
                    });
                }
            }
        }).catch((error) => {
            Swal.fire({
                title: "Fehlgeschlagen",
                text: "Leider ist das Ändern der Berechtigungen fehlgeschlagen. Mehr Infos: " + error.response.data.message,
                icon: 'error',
                });
            });
        }
    }



</script>

<template>
    <SearchFields
        :fields="filterParameters"
        @filters="filterSearch"
    />
    <div class="main-table" v-if="mediaList && mediaList.length > 0">
        <div class="inner-table">
            <table class="table">
                <thead>
                    <tr>
                        <th class="table-head">ID</th>
                        <th class="table-head">UID</th>
                        <th class="table-head">RE-Nummer</th>
                        <th class="table-head">Teilnehmer</th>
                        <th class="table-head">Nupsi</th>
                        <th class="table-head">Typ</th>
                        <th class="table-head">Fahrrad?</th>
                        <th class="table-head">Laden?</th>
                        <th class="table-head">Radtarif</th>
                        <th class="table-head">Ladetarif</th>
                        <th class="table-head">Gültig bis</th>
                        <th class="table-head">Aktionen</th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="m-datatable__row" v-for="medium of mediaList" :key="medium.id">
                        <td>{{ medium.id }}</td>
                        <td>{{ medium.identifier }}</td>
                        <td>{{ medium.Rechnungsempfaengernummer }}</td>
                        <td>{{ medium.Teilnehmer }}</td>
                        <td>{{ medium.nupsiNumber }}</td>
                        <td>{{ medium.type }}</td>
                        <td>{{ medium.can_use_bicycle }}</td>
                        <td>{{ medium.can_use_charge }}</td>
                        <td>{{ medium.bicycle_rate_id }}</td>
                        <td>{{ medium.charging_rate_id }}</td>
                        <td>{{ medium.was_used_until }}</td>
                        <td>
                            <div class="buttons d-grid gap-1 mx-auto">
                                <button class="btn btn-primary"
                                    role="button"
                                    @click.prevent="deleteMedium(medium.id)">
                                    Löschen
                                </button>
                                <button class="btn btn-primary"
                                    role="button"
                                    @click.prevent="changePermission(props.changeBikePermissionUrl, medium.id)">
                                    Fahrradberechtigung ändern
                                </button>
                                <button class="btn btn-primary"
                                    role="button"
                                    @click.prevent="changePermission(props.changeChargePermissionUrl, medium.id)">
                                    Ladeberechtigung ändern
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-primary"
                                    data-bs-toggle="modal"
                                    data-bs-target="#modalChangeMedium"
                                    @click.prevent="mediumToChange = medium.id"
                                    >
                                    Zugangsmedium bearbeiten
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="table-footer middle" v-if="mediaList && mediaList.length > 0 && footerPageNumbers.length > 1">
        <ul class="pagination">
            <li class="page-item">
                <button class="page-link" :disabled="isButtonDisabled('start', currentPage, maxPageNumber)" @click="getNewPage(currentPage - 1)">
                    <i class="fas fa-lg fa-caret-left"></i>
                </button>
            </li>
            <li v-for="page in footerPageNumbers" :key="page.id" :class="{ 'active' : page == currentPage }" >
                <button class="page-link" @click="getNewPage(page)" :disabled="isButtonDisabled(page, currentPage, maxPageNumber)">{{ page }}</button>
            </li>
            <li class="page-item">
                <button class="page-link" @click="getNewPage(currentPage + 1)" :disabled="isButtonDisabled('end', currentPage, maxPageNumber)">
                    <i class="fas fa-lg fa-caret-right"></i>
                </button>
            </li>
        </ul>
    </div>

    <div class="m-3" v-if="mediaList && (mediaList.length == 0 || mediaList == null)">
        <div class="alert alert-custom" role="alert">
            <i class="fa-solid fa-comment fa-4x"></i>
            <span>Es wurden keine wartenden Anträge gefunden.</span>
        </div>
    </div>

    <div class="modal fade" id="modalChangeMedium" tabindex="-1">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title">Zugangsmedium bearbeiten</h4>
                    <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                </div>
                <div class="modal-body">
                    <AuthMediaAdd
                        :saveUrl="replaceIDinUrl(props.changeMediumUrl, mediumToChange)"
                        :getUrl="replaceIDinUrl(props.getSingleMediumUrl, mediumToChange)"
                    />
                </div>
            </div>
        </div>
    </div>

</template>
